import { Link } from "react-router-dom";
import { AlertCircle, Gift, Plus, Users } from "lucide-react";
import { Badge } from "@/components/ui";
import { CardGridSkeleton } from "@/components/marketplace";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/contexts/AuthContext";
import {
  useMyCampaigns,
  useOpenCampaigns,
  type Campaign,
} from "@/hooks/useMarketplaceCampaigns";
import { CAMPAIGN_TONE } from "./statusTone";
import { COUNTRY_LABELS } from "./constants";

function Money({ campaign }: { campaign: Campaign }) {
  const { t } = useTranslation();

  if (campaign.compensationType === "GIFTING") {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-bold text-emerald-700">
        <Gift className="w-3.5 h-3.5" />
        {t("marketplace.campaign.giftingOnly")}
      </span>
    );
  }

  if (campaign.budgetTotal === null) {
    return <span className="text-sm text-neutral-400">—</span>;
  }

  return (
    <span className="text-sm font-bold text-neutral-900">
      {campaign.currency} {campaign.budgetTotal.toLocaleString("es")}
    </span>
  );
}

function CampaignCard({ campaign, showBrand }: { campaign: Campaign; showBrand?: boolean }) {
  const { t } = useTranslation();
  const deliverableCount =
    campaign.deliverablesSpec?.reduce((sum, d) => sum + d.quantity, 0) ?? 0;

  return (
    <Link
      to={`/campanas/${campaign.id}`}
      className="bg-white border border-neutral-200 rounded-card p-5 flex flex-col gap-3 hover:border-neutral-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-sm text-neutral-900 truncate">{campaign.title}</p>
          <p className="text-xs text-neutral-400 mt-0.5">
            {showBrand && campaign.brandProfile
              ? campaign.brandProfile.companyName
              : t(`marketplace.objectives.${campaign.objective}`)}
          </p>
        </div>
        <Badge variant={CAMPAIGN_TONE[campaign.status]} stamp className="shrink-0">
          {t(`marketplace.campaignStatus.${campaign.status}`)}
        </Badge>
      </div>

      {campaign.targetCountries.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {campaign.targetCountries.slice(0, 4).map((c) => (
            <span
              key={c}
              className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600"
            >
              {COUNTRY_LABELS[c] ?? c}
            </span>
          ))}
        </div>
      )}

      {/* A draft that cannot publish is the single most useful thing to show a
          brand on this screen, so it outranks the metrics row. */}
      {campaign.status === "DRAFT" && campaign.missingForPublish.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-700">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          {t("marketplace.campaign.missingCount", {
            count: campaign.missingForPublish.length,
          })}
        </p>
      )}

      <div className="mt-auto pt-3 border-t border-neutral-100 flex items-end justify-between gap-2">
        <div>
          <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest">
            {t("marketplace.campaign.deliverables")}
          </p>
          <p className="text-sm font-bold text-neutral-900 inline-flex items-center gap-1">
            {deliverableCount || "—"}
            {campaign.targetCreatorCount ? (
              <span className="text-xs font-normal text-neutral-400 inline-flex items-center gap-0.5">
                <Users className="w-3 h-3" />
                {campaign.targetCreatorCount}
              </span>
            ) : null}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest">
            {t("marketplace.campaign.budget")}
          </p>
          <Money campaign={campaign} />
        </div>
      </div>
    </Link>
  );
}

function BrandCampaigns() {
  const { t } = useTranslation();
  const { campaigns, loading } = useMyCampaigns();

  if (loading && campaigns.length === 0) {
    return <CardGridSkeleton />;
  }

  return (
    <>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-neutral-900">
            {t("marketplace.campaign.myTitle")}
          </h1>
          <p className="text-sm text-neutral-500 mt-1">{t("marketplace.campaign.mySubtitle")}</p>
        </div>
        <Link
          to="/campanas/nueva"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-button bg-gradient-to-r from-steel-600 to-steel-700 text-white text-sm font-medium hover:from-steel-500 hover:to-steel-600 transition-all flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          {t("marketplace.campaign.new")}
        </Link>
      </div>

      {campaigns.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-sm font-medium text-neutral-900">
            {t("marketplace.campaign.emptyTitle")}
          </p>
          <p className="text-sm text-neutral-500 mt-1">{t("marketplace.campaign.emptyBody")}</p>
          <Link
            to="/campanas/nueva"
            className="inline-block mt-4 text-sm font-medium text-steel-600 hover:text-steel-700"
          >
            {t("marketplace.campaign.new")}
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {campaigns.map((c) => (
            <CampaignCard key={c.id} campaign={c} />
          ))}
        </div>
      )}
    </>
  );
}

function CreatorCampaigns() {
  const { t } = useTranslation();
  const { campaigns, loading } = useOpenCampaigns();

  if (loading && campaigns.length === 0) {
    return <CardGridSkeleton />;
  }

  return (
    <>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-neutral-900">{t("marketplace.campaign.openTitle")}</h1>
        <p className="text-sm text-neutral-500 mt-1">{t("marketplace.campaign.openSubtitle")}</p>
      </div>

      {campaigns.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-sm font-medium text-neutral-900">
            {t("marketplace.campaign.noOpenTitle")}
          </p>
          <p className="text-sm text-neutral-500 mt-1">{t("marketplace.campaign.noOpenBody")}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {campaigns.map((c) => (
            <CampaignCard key={c.id} campaign={c} showBrand />
          ))}
        </div>
      )}
    </>
  );
}

export default function CampaignsPage() {
  const { user } = useAuth();

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {user?.marketplaceRole === "CREATOR" ? <CreatorCampaigns /> : <BrandCampaigns />}
    </div>
  );
}
